import React, { ChangeEvent, useCallback, useEffect, useState } from 'react'

import { v4 as uuidv4 } from 'uuid'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import ListCheck from '@interco/icons/core/action-navigation/ic_checklist'
import CheckBack from '@interco/icons/core/finance/ic_money_check_back'
import FrameDocument from '@interco/icons/core/action-navigation/ic_expand'
import Attachment from '@interco/icons/core/action-navigation/ic_attachment'
import CameraOn from '@interco/icons/core/media-communication/ic_photo'
import { BottomSheet } from '@interco/inter-ui/components/BottomSheet'
import {
  sendToS3Foto,
  setAnexosFoto,
  setIsErrorFoto,
  setIsLoadingFoto,
} from '@store/refinanciamento/fotoDocumentos/actions'
import { ApplicationState } from '@store/types'
import { BridgeService } from '@services/bridge'
import { Button } from '@interco/inter-ui/components/Button'
import { StepRefin } from '@utils/enums'
import { Page } from '@templates'
import routes from '@routes/routes'
import { ColorWrapper, TextParagraph, Loading } from '@atoms'
import { Colors } from '@utils/Colors'
import { trackingRefin } from '@store/refinanciamento/simulacao/actions'
import Tag from '@utils/Tag'
import { fileToBase64 } from '@utils/functions'
import { useTrackingRefin } from '@hooks'

import { Container, CustomButton, DivLine, InputFile, LineDescription } from '../styles'

const MaxFileSize = 2000
const ModoEnvio = () => {
  useTrackingRefin(StepRefin.MODO_ENVIO)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [uploadedCount, setUploadedCount] = useState(0)
  const [uploadedQuantity, setUploadedQuantity] = useState(0)
  const [bottomSheetQuantity, setBottomSheetQuantity] = useState(false)
  const [openLoading, setOpenLoading] = useState(false)
  const [bottomSheetSize, setBottomSheetSize] = useState(false)
  const { anexos, isLoading, isDone } = useSelector(
    (state: ApplicationState) => state.refinanciamento.fotoDocumentos,
  )
  const { isApp } = useSelector((state: ApplicationState) => state.ui.navigation)
  const anexosSizes = anexos?.map((anexo) => anexo?.anexo?.size)
  const somaSizes =
    Number(anexosSizes.reduce((total, numero) => Number(total) + Number(numero), 0)) / 1000

  const anexarDocumentos = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target?.files) {
      const files = Array.from(e.target.files)
      setUploadedQuantity(files.length)
      const size = files && files.reduce((sum, file) => sum + file.size, 0) / 1000

      if (files?.length > 2) {
        setBottomSheetQuantity(true)
      } else if ((size && size > MaxFileSize) || somaSizes + Number(size) > MaxFileSize) {
        setBottomSheetSize(true)
      } else {
        files?.forEach((file) => {
          const reader = new window.FileReader()
          reader.onloadend = async () => {
            if (file?.type === 'application/pdf') {
              dispatch(
                setAnexosFoto({
                  anexo: {
                    id: uuidv4(),
                    nome: file.name,
                    tipoExtensao: file.type,
                    size: file.size,
                    blobs: [
                      {
                        height: 200,
                        width: 200,
                        data: await fileToBase64(file),
                      },
                    ],
                  },
                  tipo: 'RG_CNH',
                }),
              )
              setUploadedCount((prevCount) => prevCount + 1)
            } else {
              const image = new Image()
              image.src = reader.result as string
              image.onload = () => {
                dispatch(
                  setAnexosFoto({
                    anexo: {
                      id: uuidv4(),
                      nome: file?.name || '',
                      tipoExtensao: file?.type || '',
                      size: file?.size,
                      blobs: [
                        {
                          height: image.height,
                          width: image.width,
                          data: image.src,
                        },
                      ],
                    },
                    tipo: 'RG_CNH',
                  }),
                )
                setUploadedCount((prevCount) => prevCount + 1)
              }
            }
          }

          if (file?.type === 'application/pdf') {
            reader.readAsArrayBuffer(file)
          } else {
            reader.readAsDataURL(file as Blob)
          }
        })
      }
    }
  }

  const sendPhoto = useCallback(() => {
    dispatch(setIsLoadingFoto(true))
    dispatch(setIsErrorFoto(false))
    dispatch(
      sendToS3Foto({
        tipo: 'RG_CNH',
        anexos,
      }),
    )
  }, [anexos, dispatch])

  const handleCloseSizeError = () => {
    BridgeService.tic({
      name: 'btn_fechar_erro_tamanho',
      action: 'fechar_erro_tamanho',
    })

    if (isApp) {
      Tag.enviarTagueamentoApp({
        screen: 'clt:refin:documents',
        ref_type: 'botao',
        content_action: 'Toque',
        action_id: 'Envio de documentos',
        section: 'documents',
        sub_section: 'tips',
        step: '',
        scenario: 'Envio de documentos',
        ref_figma: '2',
      })
    } else {
      Tag.enviarTagueamentoWeb({
        element_action: 'click button',
        element_name: 'Envio de documentos',
        c_page: window.location.href,
        redirect_url: '',
        section_name: 'payrollloan:refin:upload',
        section: 'upload',
        sub_section: 'tips',
        step: '',
        scenario: 'Envio de documentos',
      })
    }

    setBottomSheetSize(false)
  }

  const handleCloseQuantityError = () => {
    BridgeService.tic({
      name: 'btn_fechar_erro_quantidade',
      action: 'fechar_erro_quantidade',
    })

    if (isApp) {
      Tag.enviarTagueamentoApp({
        screen: 'clt:refin:documents',
        ref_type: 'botao',
        content_action: 'Toque',
        action_id: 'Envio de documentos',
        section: 'documents',
        sub_section: 'tips',
        step: '',
        scenario: 'Envio de documentos',
        ref_figma: '2',
      })
    } else {
      Tag.enviarTagueamentoWeb({
        element_action: 'click button',
        element_name: 'Envio de documentos',
        c_page: window.location.href,
        redirect_url: '',
        section_name: 'payrollloan:refin:upload',
        section: 'upload',
        sub_section: 'tips',
        step: '',
        scenario: 'Envio de documentos',
      })
    }

    setBottomSheetQuantity(false)
  }

  useEffect(() => {
    BridgeService.tac({
      name: 'modo-envio',
      actions: ['solicitar_documento_id'],
    })

    if (isApp) {
      Tag.enviarTagueamentoApp({
        screen: 'clt:refin:documents',
        ref_type: '',
        content_action: 'Toque',
        action_id: 'Envio de documentos',
        section: 'documents',
        sub_section: '',
        step: '',
        scenario: 'Envio de documentos',
        ref_figma: '2',
      })
    } else {
      Tag.enviarTagueamentoWeb({
        element_action: 'page_loaded',
        element_name: 'Envio de documentos',
        c_page: window.location.href,
        redirect_url: '',
        section_name: 'payrollloan:refin:upload',
        section: 'upload',
        sub_section: '',
        step: '',
        scenario: 'Envio de documentos',
      })
    }
  }, [isApp])

  useEffect(() => {
    if (isDone) {
      if (isApp) {
        Tag.enviarTagueamentoApp({
          screen: 'clt:refin:documents',
          ref_type: '',
          content_action: 'Dado carregado',
          action_id: 'Envio de documentos',
          section: 'documents',
          sub_section: 'tips',
          step: '',
          scenario: 'Envio de documentos',
          ref_figma: '2',
        })
      } else {
        Tag.enviarTagueamentoWeb({
          element_action: 'page_loaded',
          element_name: 'Envio de documentos',
          c_page: window.location.href,
          redirect_url: routes.STATUS_DOCUMENTO,
          section_name: 'payrollloan:refin:upload',
          section: 'upload',
          sub_section: 'tips',
          step: '',
          scenario: 'Envio de documentos',
        })
      }
      navigate(routes.STATUS_DOCUMENTO)
    }
  }, [navigate, isDone, isApp])

  useEffect(() => {
    if (somaSizes > MaxFileSize) setBottomSheetSize(true)
  }, [somaSizes])

  useEffect(() => {
    if (anexos.length > 0 && uploadedQuantity === uploadedCount) {
      sendPhoto()
    }
  }, [uploadedCount, sendPhoto, anexos.length, anexos, uploadedQuantity])

  const isDesktopBrowser = BridgeService.isDesktopBrowser()

  return (
    <Page
      footerTwoButtonsSpace="180px"
      positionFooter="sticky"
      stickyContainerFooterBottom="16px"
      titleHeader="Foto do documento"
      stickyFooter={
        <>
          {!isDesktopBrowser ? (
            <CustomButton
              onClick={() => {
                dispatch(trackingRefin(StepRefin.CLICK_MODO_ENVIO_TIRAR_FOTO))
                dispatch(trackingRefin(StepRefin.FOTOGRAFAR_DOCUMENTO))
                BridgeService.tic({
                  name: 'btn_tirar_foto',
                  action: 'tirar_foto',
                })

                if (isApp) {
                  Tag.enviarTagueamentoApp({
                    screen: 'clt:refin:documents',
                    ref_type: 'botao',
                    content_action: 'Toque',
                    action_id: 'Tirar uma foto agora',
                    section: 'documents',
                    sub_section: 'tips',
                    step: '',
                    scenario: 'Tirar uma foto agora',
                    ref_figma: '2',
                  })
                } else {
                  Tag.enviarTagueamentoWeb({
                    element_action: 'click button',
                    element_name: 'Tirar uma foto agora',
                    c_page: window.location.href,
                    redirect_url: routes.FOTO_DOCUMENTO,
                    section_name: 'payrollloan:refin:upload',
                    section: 'upload',
                    sub_section: 'tips',
                    step: '',
                    scenario: 'Tirar uma foto agora',
                  })
                }

                navigate(routes.FOTO_DOCUMENTO)
              }}
            >
              <ColorWrapper color={Colors.GRAY500} fontWeight="700">
                <TextParagraph variant="body-3">Tirar uma foto agora</TextParagraph>
              </ColorWrapper>
              <CameraOn width={24} height={24} color={Colors.PRIMARY500} />
            </CustomButton>
          ) : (
            <></>
          )}

          <CustomButton
            onClick={() => {
              dispatch(trackingRefin(StepRefin.CLICK_MODO_ENVIO_ANEXAR_DOCUMENTO))
              BridgeService.tic({
                name: 'btn_anexar_foto',
                action: 'anexar_foto',
              })

              if (isApp) {
                Tag.enviarTagueamentoApp({
                  screen: 'clt:refin:documents',
                  ref_type: 'botao',
                  content_action: 'Toque',
                  action_id: 'Prefiro anexar a foto',
                  section: 'documents',
                  sub_section: 'tips',
                  step: '',
                  scenario: 'Prefiro anexar a foto',
                  ref_figma: '2',
                })
              } else {
                Tag.enviarTagueamentoWeb({
                  element_action: 'click button',
                  element_name: 'Prefiro anexar a foto',
                  c_page: window.location.href,
                  redirect_url: routes.FOTO_DOCUMENTO,
                  section_name: 'payrollloan:refin:upload',
                  section: 'upload',
                  sub_section: 'tips',
                  step: '',
                  scenario: 'Prefiro anexar a foto',
                })
              }

              document.getElementById('input-camera')?.click()
            }}
          >
            <ColorWrapper color={Colors.GRAY500} fontWeight="700">
              <TextParagraph variant="body-3">
                {isDesktopBrowser ? 'Anexar o documento' : 'Prefiro anexar o documento'}
              </TextParagraph>
            </ColorWrapper>
            <Attachment width={24} height={24} color={Colors.PRIMARY500} />
            <InputFile
              id="input-camera"
              type="file"
              multiple
              accept="image/*, application/pdf"
              onChange={async (e) => {
                dispatch(trackingRefin(StepRefin.ANEXAR_DOCUMENTO))

                BridgeService.tic({
                  name: 'input_file_documento',
                  action: 'selecionar_arquivo_documento',
                })

                anexarDocumentos(e)
              }}
            />
          </CustomButton>
        </>
      }
    >
      <ColorWrapper color={Colors.GRAY500} fontWeight="600" margin="0 0 16px 0">
        <TextParagraph variant="headline-h1">
          Para enviar o documento, certifique-se que:
        </TextParagraph>
      </ColorWrapper>
      <Container>
        <LineDescription>
          <ListCheck width={24} height={24} color={Colors.GRAY500} />
          <DivLine>
            <TextParagraph variant="body-3">
              É possível ler todas as informações do documento na foto.
            </TextParagraph>
          </DivLine>
        </LineDescription>
        <LineDescription>
          <CheckBack width={24} height={24} color={Colors.GRAY500} />
          <DivLine>
            <TextParagraph variant="body-3">
              Não há reflexos na foto. Se precisar, retire o plástico protetor.
            </TextParagraph>
          </DivLine>
        </LineDescription>
        <LineDescription>
          <FrameDocument width={24} height={24} color={Colors.GRAY500} />
          <DivLine>
            <TextParagraph variant="body-3">
              O documento está centralizado e que nada está na frente dele.
            </TextParagraph>
          </DivLine>
        </LineDescription>
      </Container>
      {isLoading || openLoading ? (
        <BottomSheet
          onClose={() => {
            if (isApp) {
              Tag.enviarTagueamentoApp({
                screen: 'clt:refin:documents',
                ref_type: 'botao',
                content_action: 'Toque',
                action_id: 'Carregando arquivo',
                section: 'documents',
                sub_section: 'tips',
                step: '',
                scenario: 'Carregando arquivo',
                ref_figma: '2',
              })
            } else {
              Tag.enviarTagueamentoWeb({
                element_action: 'click button',
                element_name: 'Carregando arquivo',
                c_page: window.location.href,
                redirect_url: '',
                section_name: 'payrollloan:refin:upload',
                section: 'upload',
                sub_section: 'tips',
                step: '',
                scenario: 'Carregando arquivo',
              })
            }
            setOpenLoading(false)
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Loading width={30} height={30} />
          </div>

          <ColorWrapper
            color={Colors.GRAY500}
            fontWeight="700"
            margin="24px 0"
            style={{ textAlign: 'center' }}
          >
            <TextParagraph variant="body-1">Carregando arquivo...</TextParagraph>
          </ColorWrapper>
        </BottomSheet>
      ) : (
        <></>
      )}
      {bottomSheetSize ? (
        <BottomSheet onClose={handleCloseSizeError}>
          <ColorWrapper color={Colors.GRAY500} fontWeight="600" margin="0px 0 16px 0">
            <TextParagraph variant="headline-h3">Tamanho máximo excedido</TextParagraph>
          </ColorWrapper>
          <TextParagraph variant="body-3">
            Os arquivos enviados devem somar no máximo 2MB de tamanho. Verifique o tamanho e tente
            novamente.
          </TextParagraph>
          <br />
          <br />
          <Button fullWidth onClick={handleCloseSizeError}>
            Fechar
          </Button>
        </BottomSheet>
      ) : (
        <></>
      )}
      {bottomSheetQuantity ? (
        <BottomSheet onClose={handleCloseQuantityError}>
          <ColorWrapper color={Colors.GRAY500} fontWeight="600" margin="0px 0 16px 0">
            <TextParagraph variant="headline-h3">Quantidade máxima excedida</TextParagraph>
          </ColorWrapper>
          <TextParagraph variant="body-3">
            Envie no máximo 2 arquivos contendo frente e verso. Caso frente e verso não estejam
            presentes em apenas um arquivo.
          </TextParagraph>
          <br />
          <br />
          <Button fullWidth onClick={handleCloseQuantityError}>
            Fechar
          </Button>
        </BottomSheet>
      ) : (
        <></>
      )}
    </Page>
  )
}
export default ModoEnvio
